import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { homePath } from "@/lib/accountAccess";
import api, { apiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const submit = async event => {
    event.preventDefault(); setLoading(true); setError(""); setMessage("");
    try { const user = await login(username, password); navigate(homePath(user), { replace: true }); }
    catch (e) { setError(apiError(e.response?.data?.detail) || "Login failed"); }
    finally { setLoading(false); }
  };
  const recovery = async () => {
    setError("");
    try { const res = await api.post("/auth/forgot-password"); setMessage(res.data.message); }
    catch (e) { setError(apiError(e.response?.data?.detail)); }
  };
  return <div className="min-h-screen grid lg:grid-cols-2">
    <div className="hidden lg:flex flex-col justify-between bg-navy text-white p-12 relative overflow-hidden">
      <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-maroon/40 blur-3xl" />
      <div className="flex items-center gap-3 relative"><img src="/finlend-logo.png" alt="FinLend" className="h-14 w-14 rounded-full object-contain bg-white" /><span className="font-heading font-bold text-2xl">FinLend</span></div>
      <div className="relative"><h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">Money Lending &amp;<br />Loan Management</h1><p className="mt-4 text-slate-300 max-w-md text-base">Track customers, manage weekly EMI schedules, record Cash/UPI payments and generate profit &amp; loss reports — all in one professional workspace.</p></div>
      <div className="relative text-slate-400 text-sm">Secure • Reliable • Made for lenders</div>
    </div>
    <div className="flex items-center justify-center p-6 bg-[#f8fafc]"><div className="w-full max-w-sm">
      <div className="lg:hidden flex items-center justify-center gap-3 mb-8"><img src="/finlend-logo.png" alt="FinLend" className="h-12 w-12 object-contain" /><span className="font-heading font-bold text-xl text-navy">FinLend</span></div>
      <form onSubmit={submit} className="space-y-5">
        <div><h2 className="font-heading text-2xl font-bold text-navy">Welcome back</h2><p className="text-slate-500 text-sm mt-1">Sign in using the login issued by your FinLend administrator.</p></div>
        <div><Label htmlFor="login-username">Username</Label><Input id="login-username" data-testid="login-username-input" required value={username} onChange={e => setUsername(e.target.value)} autoComplete="username" autoFocus spellCheck={false} /></div>
        <div><Label htmlFor="login-password">Password</Label><Input id="login-password" data-testid="login-password-input" required type="password" value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" /></div>
        {error && <p role="alert" data-testid="login-error" className="text-sm text-red-600">{error}</p>}
        {message && <p role="status" className="text-sm text-slate-600">{message}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full bg-maroon hover:bg-maroon-hover h-11">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}</Button>
        <button type="button" onClick={recovery} className="text-sm text-accentblue w-full hover:underline">Forgot password?</button>
        <p className="text-xs text-slate-500 text-center">New business or staff access? Contact Akash to create your login.</p>
      </form>
    </div></div>
  </div>;
}
